// Pure helpers for file-tree mutation flows in the SPA.
// No DOM, no React. Tree node shape mirrors `flattenVisible` in
// packages/ui-web/src/app.jsx — folders carry `children`, files carry
// `path` and `name`.

export function siblingNames(tree, parentPath) {
  if (!parentPath) {
    return new Set(tree.map((n) => n.name));
  }
  const parts = parentPath.split('/');
  let cur = tree;
  for (const part of parts) {
    const folder = cur.find((n) => n.type === 'folder' && n.name === part);
    if (!folder) return new Set();
    cur = folder.children;
  }
  return new Set(cur.map((n) => n.name));
}

// Split "foo.md" -> { base: "foo", ext: ".md" }. Names without a dot
// (LICENSE) or beginning with a dot (.env) treat the whole string as
// the base and the suffix as "".
function splitExt(name) {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0) return { base: name, ext: '' };
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot) };
}

export function nextDuplicateName(baseName, existingNames) {
  const { base, ext } = splitExt(baseName);
  let candidate = `${base}-copy${ext}`;
  if (!existingNames.has(candidate)) return candidate;
  for (let i = 2; i < 1000; i++) {
    candidate = `${base}-copy-${i}${ext}`;
    if (!existingNames.has(candidate)) return candidate;
  }
  // Pathological: 1000 collisions. Fall back to timestamp suffix.
  return `${base}-copy-${Date.now()}${ext}`;
}

// eslint-disable-next-line no-control-regex
const CONTROL_OR_BACKSLASH = /[\\\x00-\x1f]/;

export function validateBasename(name, existingNames, opts = {}) {
  const kind = opts.kind || 'file';
  const trimmed = (name || '').trim();
  if (trimmed === '') return { ok: false, reason: 'Name is required.' };
  if (trimmed === '.' || trimmed === '..') {
    return { ok: false, reason: 'Name is reserved.' };
  }
  if (trimmed.includes('/')) {
    return { ok: false, reason: 'Name may not contain "/".' };
  }
  if (CONTROL_OR_BACKSLASH.test(trimmed)) {
    return { ok: false, reason: 'Name contains an invalid character.' };
  }
  if (kind === 'file' && !trimmed.toLowerCase().endsWith('.md')) {
    return { ok: false, reason: 'Name must end in .md.' };
  }
  if (existingNames.has(trimmed)) {
    return { ok: false, reason: 'A sibling with that name already exists.' };
  }
  return { ok: true };
}

export function replacePathPrefix(path, oldPrefix, newPrefix) {
  if (path === oldPrefix) return newPrefix;
  if (path.startsWith(oldPrefix + '/')) {
    return newPrefix + path.slice(oldPrefix.length);
  }
  return path;
}

export function joinPath(parent, basename) {
  return parent ? `${parent}/${basename}` : basename;
}

export function splitBasename(path) {
  const i = path.lastIndexOf('/');
  if (i < 0) return { parent: '', basename: path };
  return { parent: path.slice(0, i), basename: path.slice(i + 1) };
}
